/**
 * useEdgeSwipe — iOS-style "swipe right from the left edge" gesture.
 *
 * PWAs and installed React apps don't get the native browser swipe-back
 * behaviour. This hook fakes it: a touch that starts within `edgePx`
 * pixels of the left edge and drags right by at least `minDist` pixels
 * (with vertical drift under `maxVertical`) invokes `onSwipeRight`.
 *
 * Silent no-op on non-touch devices — mouse users lose nothing.
 *
 * Usage:
 *
 *     useEdgeSwipe({
 *       onSwipeRight: () => nav(-1),
 *       enabled: !modalOpen,
 *     });
 */
import { useEffect, useRef } from "react";

export default function useEdgeSwipe({
  onSwipeRight,
  edgePx = 32,
  minDist = 60,
  maxVertical = 50,
  enabled = true,
} = {}) {
  // Latest callback ref — so we don't rebind listeners every render.
  const cbRef = useRef(onSwipeRight);
  useEffect(() => { cbRef.current = onSwipeRight; }, [onSwipeRight]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;
    if (!("ontouchstart" in window)) return undefined;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onStart = (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      // Only fire when the finger lands near the left edge — matches
      // iOS Safari's swipe-back affordance and avoids accidentally
      // hijacking horizontal scrollers in the middle of the page.
      if (t.clientX > edgePx) return;
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    };

    const onEnd = (e) => {
      if (!tracking) return;
      tracking = false;
      const t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (dx >= minDist && Math.abs(dy) <= maxVertical) {
        try { cbRef.current && cbRef.current(); } catch (_) { /* noop */ }
      }
    };

    const onCancel = () => { tracking = false; };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onCancel, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onCancel);
    };
  }, [enabled, edgePx, minDist, maxVertical]);
}
